// src/modules/manualJournal/components/AddTransactionModal.jsx

import React, { useState } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, Button, Space, message, Alert, DatePicker,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createTransaction } from '../api';
import { ACCOUNT_CATEGORIES, TRANSACTION_TYPES } from '../constants';

export default function AddTransactionModal({ journalDate, onTransactionAdded, loading }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleOpenModal = () => {
    form.resetFields();
    // Most entries record something that happened on the day-book page's own day.
    form.setFieldsValue({ transactionDate: journalDate ? dayjs(journalDate) : dayjs() });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      await createTransaction(journalDate, {
        ...values,
        // Sent as YYYY-MM-DD so the server parses it to the same start-of-day it
        // uses for the journal date, instead of shifting across the timezone.
        transactionDate: values.transactionDate?.format('YYYY-MM-DD'),
      });
      message.success('Transaction created successfully');
      setIsModalVisible(false);
      form.resetFields();
      onTransactionAdded?.();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create transaction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleOpenModal}
      >
        Add Transaction
      </Button>

      <Modal
        title="Add New Transaction"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Alert
            message="Enter transaction details"
            description={`Select From/To accounts and enter amount. The system will calculate new balances automatically. This is being booked on the ${journalDate ? dayjs(journalDate).format('DD/MM/YYYY') : 'selected'} page — set the transaction date below if the activity happened on a different day.`}
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="Transaction Date"
            name="transactionDate"
            rules={[{ required: true, message: 'Please pick the date this happened' }]}
            tooltip="When the activity actually happened. Defaults to this day-book page's date, which is the date it is being booked on."
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear={false} />
          </Form.Item>

          <Form.Item
            label="From Account"
            name="accountFrom"
            rules={[{ required: true, message: 'Please select account from' }]}
          >
            <Select
              placeholder="Select account to debit from"
              options={ACCOUNT_CATEGORIES}
            />
          </Form.Item>

          <Form.Item
            label="To Account"
            name="accountTo"
            rules={[{ required: true, message: 'Please select account to' }]}
          >
            <Select
              placeholder="Select account to credit to"
              options={ACCOUNT_CATEGORIES}
            />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter amount' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
            ]}
          >
            <InputNumber
              placeholder="0.00"
              prefix="₹"
              style={{ width: '100%' }}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea
              placeholder="Enter transaction details"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="Transaction Type"
            name="transactionType"
            initialValue="TRANSFER"
          >
            <Select
              placeholder="Select type"
              options={TRANSACTION_TYPES}
            />
          </Form.Item>

          <Form.Item
            label="Reference (Optional)"
            name="referenceNumber"
          >
            <Input
              placeholder="e.g., Invoice ID, Case ID"
            />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create Transaction
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
